import {
  Box,
  Button,
  Center,
  Flex,
  Image,
  keyframes,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import {CommonColors} from '@utils/CommonStyles'
import HelpCircle from '@assets/svgs/help-circle.svg'
import {ReactComponent as TypeSquare} from '@assets/svgs/type_square.svg'
import {ReactComponent as LightBulb} from '@assets/svgs/light_bulb.svg'
import {useDispatch, useSelector} from 'react-redux'
import {Popover} from 'antd'
import {useState, useRef, useEffect} from 'react'
import GlobalMic3 from '@components/GlobalMic3'
import SuggestionItem from './SuggestionItem'
import GlobalMicMini from '@components/GlobalMicMini'
import {
  updateIntroductionData,
  updateIsShowFooter,
} from '@redux/learn-part-three/learnPartThree'
import learnPartTwoRequest from '@apis/learn-part-two'
import Loading from '@components/Loading'
import {updateAudio} from '@redux/learn-part-three/learnPartThree'
import {audioRef} from '@utils'
import learnPartThreeRequest from '@apis/learn-part-three'

const rippleAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(2.5); opacity: 0; }
`
const slideUp = keyframes`
from { transform: translateY(20px); opacity: 0; }
to { transform: translateY(0); opacity: 1; }
`

const GetIdea = () => {
  const {currentContent} = useSelector((state: any) => state.learnPartThree)
  const [improvements, setImprovements] = useState<any>({
    statement: null,
    explanation: null,
  })
  const dispatch = useDispatch()

  useEffect(() => {
    const getAudioUrl = () => {
      const {data} = currentContent || {}
      let introductionData

      if (!improvements?.statement?.message) {
        introductionData = data?.statement_form?.introduction
        dispatch(updateIntroductionData(introductionData))

        return data?.statement_form?.introduction_audio
      }

      if (!improvements?.explanation?.message) {
        introductionData = data?.explanation_form?.introduction
        dispatch(updateIntroductionData(introductionData))

        return data?.explanation_form?.introduction_audio
      }

      introductionData = data?.final_intro
      dispatch(updateIntroductionData(introductionData))
      return data?.final_intro_audio
    }

    dispatch(
      updateAudio({
        time: Date.now(),
        url: getAudioUrl() || '',
      }),
    )
  }, [improvements, currentContent])

  useEffect(() => {
    const updateImprovements = {
      statement: currentContent?.data?.statement_form?.improvement,
      explanation: currentContent?.data?.explanation_form?.improvement,
    }

    setImprovements(updateImprovements)
  }, [currentContent])

  useEffect(() => {
    if (
      improvements?.statement?.message &&
      improvements?.explanation?.message
    ) {
      dispatch(updateIsShowFooter(true))
    } else {
      dispatch(updateIsShowFooter(false))
    }
  }, [improvements])

  return (
    <Flex gap={'24px'}>
      <Flex flex={1} animation={`${slideUp} 0.5s ease-out forwards`}>
        <GetIdeaItem
          type="statement"
          isOpen={true}
          data={currentContent?.data?.statement_form}
          improvement={improvements?.statement}
          setImprovements={setImprovements}
        />
      </Flex>
      <Flex
        flex={1}
        animation={`${slideUp} 0.5s ease-out forwards`}
        style={{
          opacity: 0,
          animationDelay: '0.5s',
        }}>
        <GetIdeaItem
          type="explanation"
          isOpen={!!improvements?.statement?.message}
          data={currentContent?.data?.explanation_form}
          improvement={improvements?.explanation}
          setImprovements={setImprovements}
        />
      </Flex>
    </Flex>
  )
}

export default GetIdea

const GetIdeaItem = ({
  type,
  data,
  isOpen,
  improvement,
  setImprovements,
}: {
  type: 'statement' | 'explanation'
  data: any
  isOpen: boolean
  improvement: any
  setImprovements: any
}) => {
  const {sessionId, currentContent} = useSelector(
    (state: any) => state.learnPartThree,
  )
  const [isRecording, setIsRecording] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [hasRecordedOnce, setHasRecordedOnce] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isShowSuggestion, setIsShowSuggestion] = useState(false)
  const [suggestionData, setSuggestionData] = useState<any>()
  const [textareaValue, setTextareaValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [randomSample, setRandomSample] = useState('')

  useEffect(() => {
    if (data?.sample?.length) {
      setRandomSample(
        data.sample[Math.floor(Math.random() * data.sample.length)],
      )
    }
  }, [data?.sample])

  useEffect(() => {
    if (type === 'explanation' && isOpen) {
      getSuggestionData()
    }
  }, [isOpen])

  const handleStartRecording = () => {
    setIsRecording(true)
    setIsPopoverOpen(true)
    if (audioRef.current) {
      audioRef.current.pause()
    }
  }

  const handleRecordingComplete = async (recordingBlob: Blob) => {
    setHasRecordedOnce(true)
    setIsRecording(false)
    setIsPopoverOpen(false)
    setIsLoading(true)

    try {
      let file = new File([recordingBlob], 'audio.wav', {type: 'audio/wav'})
      const formData = new FormData()
      formData.append('audio-file', file)
      formData.append('session-id', '')
      const res = await learnPartTwoRequest.stt(formData)
      setTextareaValue(prev => {
        const newText = res?.data?.data?.text || ''
        return prev ? `${prev} ${newText}` : newText
      })
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  const handleTypeManually = () => {
    setHasRecordedOnce(true)
    setTimeout(() => {
      textareaRef.current?.focus()
    }, 0)
  }

  const getImprovementData = async () => {
    setIsLoading(true)
    try {
      const params = {
        session_id: sessionId,
        idea: textareaValue,
        step: currentContent?.step,
        idea_type: data?.type,
      }

      const res = await learnPartThreeRequest.upgradeIdea(params)
      const improvementData = res?.data?.data
      return improvementData
    } catch (error) {
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const getSuggestionData = async () => {
    const params = {
      session_id: sessionId,
    }

    const res = await learnPartThreeRequest.getSuggestion(params)
    const data = res?.data?.data

    // Combine all values into a single string
    const allSuggestions = [
      ...(data?.chunk_suggestions?.example || []),
      ...(data?.chunk_suggestions?.explanation || []),
    ]
      .filter(Boolean)
      .join('. ')

    setSuggestionData({
      ...data,
      combinedText: allSuggestions,
    })
  }

  const handleSubmit = async () => {
    if (!textareaValue) return
    const improvement = await getImprovementData()
    if (!improvement) return
    setImprovements((prev: any) => ({...prev, [type]: improvement}))
  }

  if (!isOpen) {
    return (
      <Box w={'100%'}>
        <Flex alignItems={'center'} mb={2} gap={'4px'}>
          <Text fontSize={'16px'} fontWeight={'600'}>
            {data?.header?.text}
          </Text>
          <Text as="span" color={CommonColors.primary}>
            *
          </Text>
          <Tooltip label={data?.header?.popup} hasArrow>
            <Image
              cursor={'pointer'}
              display={'inline-block'}
              src={HelpCircle}
              alt="help"
              width={'18px'}
              height={'18px'}
            />
          </Tooltip>
        </Flex>
        <Center p={'16px'} bg={'white'} borderRadius={'12px'} color={'#667085'}>
          Trả lời Statement trước nhé!
        </Center>
      </Box>
    )
  }

  return (
    <Box w={'100%'}>
      <Flex alignItems={'center'} mb={2} gap={'4px'}>
        <Text fontSize={'16px'} fontWeight={'600'}>
          {data?.header?.text}
        </Text>
        <Text as="span" color={CommonColors.primary}>
          *
        </Text>
        <Tooltip label={data?.header?.popup} hasArrow>
          <Image
            cursor={'pointer'}
            display={'inline-block'}
            src={HelpCircle}
            alt="help"
            width={'18px'}
            height={'18px'}
          />
        </Tooltip>
      </Flex>
      {improvement?.message ? (
        <>
          <VStack
            maxWidth={'100%'}
            align="start"
            spacing={4}
            flex={1}
            p={'16px'}
            borderRadius={'12px'}>
            <Flex
              bg="blue.50"
              p={4}
              borderRadius="xl"
              width="100%"
              gap={'10px'}>
              <Text color="blue.600" fontSize={'16px'}>
                {improvement?.message?.text}
              </Text>
            </Flex>
            <Box>
              <Text mb={2} fontWeight="600" fontSize={'16px'}>
                Nâng cấp cho bạn nè!
              </Text>
              <VStack align="stretch" spacing={4}>
                {improvement?.message?.improvement?.map(
                  (suggestion: any, i: number) => (
                    <SuggestionItem key={i} suggestion={suggestion} />
                  ),
                )}
              </VStack>
            </Box>
          </VStack>
        </>
      ) : (
        <>
          <Box position="relative">
            {isLoading && <Loading />}
            <Textarea
              ref={textareaRef}
              height="300px"
              resize={'none'}
              bg={'white'}
              value={textareaValue}
              onChange={e => setTextareaValue(e.target.value)}
              isDisabled={!hasRecordedOnce}
            />
            {!hasRecordedOnce && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                zIndex={10}>
                <GlobalMic3
                  isLoading={isLoading}
                  handleStartRecording={handleStartRecording}
                  handleRecordingComplete={handleRecordingComplete}
                />
              </Box>
            )}
            {hasRecordedOnce && (
              <Box position="relative">
                {isRecording && (
                  <Box
                    position="absolute"
                    bottom="4"
                    right="4"
                    width="20px"
                    height="20px"
                    borderRadius="50%"
                    bg="#FF4D4F"
                    opacity="0.3"
                    animation={`${rippleAnimation} 1.5s infinite`}
                  />
                )}
                <Popover
                  content={
                    isRecording ? 'Bấm để dừng ghi âm' : 'Bấm để bắt đầu ghi âm'
                  }
                  trigger="hover"
                  placement="top"
                  open={isRecording || isPopoverOpen}
                  onOpenChange={visible =>
                    !isRecording && setIsPopoverOpen(visible)
                  }>
                  <Box position="absolute" bottom={4} right={4} zIndex={10}>
                    <GlobalMicMini
                      isDisabled={isLoading}
                      handleStartRecording={handleStartRecording}
                      handleRecordingComplete={handleRecordingComplete}
                    />
                  </Box>
                </Popover>
              </Box>
            )}
          </Box>
          {isShowSuggestion &&
            (type === 'explanation' ? (
              <Flex mt={'24px'}>
                {suggestionData ? (
                  <Text
                    fontSize={'14px'}
                    fontWeight={'500'}
                    color={CommonColors.primary}
                    whiteSpace="pre-wrap">
                    {suggestionData.combinedText}
                  </Text>
                ) : (
                  <Text>loading...</Text>
                )}
              </Flex>
            ) : (
              <Flex flexDirection={'column'} mt={'24px'}>
                <Text
                  as={'span'}
                  fontSize={'14px'}
                  fontWeight={'500'}
                  color={CommonColors.primary}>
                  {randomSample}
                </Text>
              </Flex>
            ))}
          <Flex
            justifyContent={hasRecordedOnce ? 'center' : 'space-between'}
            gap={'40px'}
            mt={'24px'}>
            <Button
              leftIcon={<LightBulb />}
              w={'175px'}
              variant={'outline'}
              color={CommonColors.primary}
              borderColor={CommonColors.primary}
              onClick={() => setIsShowSuggestion(!isShowSuggestion)}>
              {isShowSuggestion ? 'Ẩn gợi ý' : 'Gợi ý'}
            </Button>
            {!hasRecordedOnce && (
              <Button
                isDisabled={isRecording || isLoading}
                leftIcon={<TypeSquare />}
                w={'175px'}
                variant={'outline'}
                color={CommonColors.primary}
                borderColor={CommonColors.primary}
                onClick={handleTypeManually}>
                Nhập tay
              </Button>
            )}

            <Button
              isDisabled={!textareaValue || isRecording || isLoading}
              w={'175px'}
              variant={'outline'}
              color={CommonColors.primary}
              borderColor={CommonColors.primary}
              onClick={handleSubmit}>
              Gửi
            </Button>
          </Flex>
        </>
      )}
    </Box>
  )
}
